import { useState, type ReactNode } from 'react';
import { RefreshCw, Zap } from 'lucide-react';
import { AiSurface } from '@/components/ui/ai-surface';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { StatusChip } from '@/components/ui/status-chip';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import {
  useRecheckHealthDomainMutation,
  useRecheckReadinessCriterionMutation,
} from '@/lib/api/ai-runs';
import { cn } from '@/lib/utils/cn';

/**
 * The frames the Domains and Go-live criteria details share (prototype
 * `.dhead`, `.card.card-tight`, `.card-title-row`, `.p-label`, `.ai-box`,
 * `.oform`), in the theme's tokens, and the Re-check action (E21 T-0239).
 */

/** The uppercase overline inside a card ("Rating", "Why"). */
export const P_LABEL =
  'text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-ink-500';

/** The amber-edged form a rating, a milestone or the plan is edited in. */
export const OFORM =
  'flex flex-col gap-2.5 rounded-card border border-accent-amber-600 bg-accent-amber-50 p-4';

/** The prototype's `.input` / `textarea.input`, at the detail's size. */
export const TEXTAREA =
  'block min-h-16 w-full rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body-sm text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

/** A detail card: 16px in, the h3 on the left, a meta line or a link on the right. */
export function DetailCard({
  title,
  aside,
  label,
  children,
  className,
}: {
  title?: string;
  aside?: ReactNode;
  /** The region's name when it differs from the title (or there is none). */
  label?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-label={label ?? title}
      className={cn('min-w-0 rounded-card border border-ink-100 bg-paper-0 p-4', className)}
    >
      {title ? (
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="text-h3 font-semibold text-ink-900">{title}</h2>
          {aside}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** The meta text on a card's title row. */
export function Meta({ children }: { children: ReactNode }) {
  return <span className="text-meta text-ink-500">{children}</span>;
}

/** The small grey link on a card's title row ("All findings →"). */
export const CARD_LINK =
  'text-meta text-ink-500 hover:text-ink-900 hover:underline hover:underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

/**
 * The AI reading box (prototype `.ai-box`): the lavender surface with its
 * 2px rule, the "AI reading · confidence" title, then the body.
 */
export function AiBox({
  label = 'AI reading',
  confidence,
  isNew,
  title,
  children,
}: {
  label?: string;
  confidence?: number;
  /** Arrived after the last rating or score. */
  isNew?: boolean;
  /** Replaces "AI reading" (e.g. "No AI reading yet"). */
  title?: string;
  children: ReactNode;
}) {
  return (
    <AiSurface
      role="region"
      aria-label={label}
      className="flex flex-col gap-2 rounded-card border-0 border-l-2 border-ai-surface-border px-4 py-3.5"
    >
      <div className="flex flex-wrap items-center gap-2 text-meta font-semibold text-ai-ink">
        <Zap className="h-3.5 w-3.5" aria-hidden="true" />
        {title ?? (
          <>
            AI reading ·{confidence !== undefined ? <AiConfidenceChip value={confidence} /> : null}
            {isNew ? (
              <StatusChip tone="ai" size="sm">
                new
              </StatusChip>
            ) : null}
          </>
        )}
      </div>
      {children}
    </AiSurface>
  );
}

/** The small "AI" pill: a list row while an AI reading waits, the AI's pick on the rating scale. */
export function AiMarker({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-ai-surface-border bg-ai-surface px-1.5 text-[10px] font-semibold leading-4 text-ai-ink',
        className,
      )}
    >
      <Zap className="h-[9px] w-[9px]" aria-hidden="true" />
      AI
    </span>
  );
}

/** The 13px ink paragraph inside an AI box. */
export const AI_TEXT = 'm-0 text-body-sm text-ink-900';

/**
 * Re-check (E21 T-0239): ask the AI to read every piece of evidence for one
 * domain or criterion again. Leads and above; the API is the floor. The
 * reading lands in the AI box when the worker is done.
 */
export function useRecheck(engagementId: string, kind: 'domain' | 'criterion') {
  const domain = useRecheckHealthDomainMutation(engagementId);
  const criterion = useRecheckReadinessCriterionMutation(engagementId);
  const mutation = kind === 'domain' ? domain : criterion;
  const [askedFor, setAskedFor] = useState<string | null>(null);
  return {
    pending: mutation.isPending,
    error: mutation.error,
    askedFor,
    request: (id: string) => {
      setAskedFor(null);
      mutation.mutate(id, { onSuccess: () => setAskedFor(id) });
    },
    reset: () => {
      setAskedFor(null);
      mutation.reset();
    },
  };
}

export function RecheckButton({
  pending,
  onClick,
  what,
}: {
  pending: boolean;
  onClick: () => void;
  /** "Schedule", "UAT": names the button for a screen reader. */
  what: string;
}) {
  return (
    <WorkspaceButton
      variant="secondary"
      onClick={onClick}
      disabled={pending}
      aria-label={`Re-check ${what}`}
    >
      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
      {pending ? 'Asking…' : 'Re-check'}
    </WorkspaceButton>
  );
}
